// @ts-strict-ignore
/* eslint no-unused-vars: 0 */
import {AttrsSchema, Component} from 'panel';

import {commaizeNumber} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {djangoFetch} from 'common/report/queries/utils';

import css from './index.inline.styl';
import {template} from './template';
import {CancelAccountDialogAttrs, CancelAccountDialogState} from './types';

const SELECTED_ANOTHER_VENDOR = `I have selected another vendor`;
const OTHER = `Other`;
const LACK_OF_A_FEATURE = `Mixpanel lacks a feature we need`;
const DEFAULT_DROPDOWN = `Select one`;
const ANALYTICS_ALTERNATIVES = [
  DEFAULT_DROPDOWN,
  `I will continue using Mixpanel's free product`,
  SELECTED_ANOTHER_VENDOR,
  `I no longer have a need for analytics`,
  `I have internal resources which will satisfy our analytics needs`,
];
const COMPETITORS = [
  DEFAULT_DROPDOWN,
  `Adobe Analytics`,
  `Amplitude`,
  `Customer.io`,
  `Clevertap`,
  `Firebase`,
  `Google Analytics (Free)`,
  `Google Analytics 360`,
  `Heap`,
  `Intercom`,
  `Kissmetrics`,
  `Leanplum`,
  `Pendo`,
  `Woopra`,
  OTHER,
];

interface CancelAccountDialogJsxProps {
  attrs?: CancelAccountDialogAttrs;
  on?: {
    close?: (ev) => void;
    submit?: (ev) => void;
  };
}

export class CancelAccountDialog extends Component<CancelAccountDialogState, CancelAccountDialogAttrs> {
  static get attrsSchema(): AttrsSchema<CancelAccountDialogAttrs> {
    return {
      ...super.attrsSchema,
      open: {type: `boolean`, description: `Whether the dialog should be open`},
      'account-id': {type: `number`, description: `Account ID`},
      'days-to-churn': {type: `number`, description: `Days it takes for an org to churn`},
      'downgrade-reasons': {type: `json`, description: `Array of cancellation reasons to display`},
      plans: {
        type: `json`,
        default: {
          people: {isFree: true},
          events: {isFree: true},
        },
        description: `Account's plan information`,
      },
      'request-type': {type: `string`, description: `Kind of request (deletion_type or downgrade_type`},
      'is-legacy': {
        type: `boolean`,
        description: `If canceling a legacy plan. Allows cancelling either the events or people legacy plan.`,
      },
    };
  }
  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        loading: false,
        planTypes: {
          events: false,
          people: false,
        },
        reasons: [],
        featureInput: null,
        alternativesValue: DEFAULT_DROPDOWN,
        selectedVendor: DEFAULT_DROPDOWN,
        vendorInput: null,
        additionalInfo: null,
        validationMessage: null,
        validationType: null,
        waitingForResponse: false,
      },
      helpers: {
        ANALYTICS_ALTERNATIVES,
        COMPETITORS,
        canCancelPlan: (planType) => {
          return !this.attr(`plans`)[planType].isFree;
        },
        handleChange: (ev) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.dispatchEvent(new CustomEvent(`close`));
          }
        },
        handleCancel: () => {
          this.dispatchEvent(new CustomEvent(`close`, {}));
        },
        handleSubmit: async () => {
          if (this.validateForm()) {
            const result = await this.submitCancelRequest(this.getRequestData());
            if (result) {
              this.update(this.config.defaultState);
            }
          }
        },
        isFree: () => this.isFreeAccount(),
        shouldShowSurvey: () => {
          return !(this.isFreeAccount() && this.attr(`request-type`) === `deletion_type`);
        },
        shouldShowFeatureInput: () => this.state.reasons.includes(LACK_OF_A_FEATURE),
        shouldShowVendor: () => this.state.alternativesValue === SELECTED_ANOTHER_VENDOR,
        shouldShowVendorInput: () =>
          this.state.alternativesValue === SELECTED_ANOTHER_VENDOR && this.state.selectedVendor === OTHER,
        getModalHeader: () =>
          this.attr(`request-type`) === `downgrade_type` ? `Downgrade Plan` : `Delete Organization`,
        getPlanDescription: (planType) => {
          const plan = this.attr(`plans`)[planType];
          const priceStr = `$` + commaizeNumber(plan.price);
          const termStr = this.formatTerm(plan.term);
          return `You are currently subscribed to the "${plan.name}" plan (${priceStr}${termStr}).`;
        },
        getReasonOptions: () => {
          return this.attr(`downgrade-reasons`)?.map((reason) =>
            Object.assign(
              {},
              {
                label: reason,
                value: reason,
                isSelected: this.state.reasons.includes(reason),
              },
            ),
          );
        },
        updatePlanTypes: (planType) => {
          const isChecked = this.state.planTypes[planType];
          const planTypes = Object.assign({}, this.state.planTypes);
          planTypes[planType] = !isChecked;
          this.update({planTypes});
          this.resetValidationError(`plan`);
        },
        selectedReason: (item) => {
          const reasons = new Set(this.state.reasons);
          if (item.isSelected) {
            reasons.add(item.value);
          } else {
            reasons.delete(item.value);
          }
          this.update({reasons: [...reasons]});
          this.resetValidationError(`reason`);
        },
        updateAlternatives: (item) => {
          this.update({alternativesValue: item.detail.value});
          this.resetValidationError(`analytics`);
        },
        updateCompetitor: (item) => {
          this.update({selectedVendor: item.detail.value});
          this.resetValidationError(`vendor`);
        },
        updateFeatureInput: (ev) => {
          this.update({featureInput: ev.target.value});
          this.resetValidationError(`feature`);
        },
        updateVendorInput: (ev) => {
          this.update({vendorInput: ev.target.value});
          this.resetValidationError(`vendor`);
        },
        updateAdditionalInfo: (ev) => this.update({additionalInfo: ev.target.value}),
      },
    };
  }

  private getCancelledPlanResourceType(cancelledPlanTypes: string[] | undefined): string {
    // MTU and Pricing 2023 plans (and any other non-legacy plans that may have
    // been imagined since) don't have real people plans, so users can't have
    // cancelled a people plan. Therefore, we filter out people plan types before
    // determining what the resource type is. That leaves only mtu and engagement
    // types, which makes the reduction straightforward.
    return (
      cancelledPlanTypes
        ?.filter((planType) => {
          return planType !== `people`;
        })
        .map((planType) => {
          return planType === `mtus` ? `MTU` : `Engagement`;
        })
        .reduce((primaryType, resourceType) => {
          return primaryType === `Engagement` ? resourceType : primaryType;
        }, `Engagement`) || `Engagement`
    );
  }

  formatTerm(termLengthInMonths) {
    const termLengthInYears = termLengthInMonths / 12;
    if (termLengthInYears % 1) {
      return termLengthInMonths === 1 ? `/mo` : ` every ${termLengthInMonths} months`;
    }
    return termLengthInYears === 1 ? `/yr` : ` every ${termLengthInYears} years`;
  }

  isFreeAccount() {
    return Object.values(this.attr(`plans`)).every((plan) => plan.isFree);
  }

  resetValidationError(type) {
    if (this.state.validationType === type) {
      this.update({validationType: null, validationMessage: null});
    }
  }

  validateForm() {
    let validationType = null,
      validationMessage = null;
    if (
      this.attr(`is-legacy`) &&
      this.attr(`request-type`) === `downgrade_type` &&
      Object.values(this.state.planTypes).every((planTypeSelected) => !planTypeSelected)
    ) {
      validationType = `plan`;
      validationMessage = `Select at least one downgrade plan`;
    } else if (this.config.helpers.shouldShowSurvey()) {
      if (this.state.reasons.length === 0) {
        validationType = `reason`;
        validationMessage = `Select at least one reason`;
      } else if (this.state.reasons.includes(LACK_OF_A_FEATURE) && !this.state.featureInput) {
        validationType = `feature`;
        validationMessage = `Tell us what feature is missing`;
      } else if (this.state.alternativesValue === DEFAULT_DROPDOWN) {
        validationType = `analytics`;
        validationMessage = `Select one option from the dropdown`;
      } else if (
        this.state.alternativesValue === SELECTED_ANOTHER_VENDOR &&
        this.state.selectedVendor === DEFAULT_DROPDOWN
      ) {
        validationType = `vendor`;
        validationMessage = `Select the vendor you plan to switch to`;
      } else if (
        this.state.alternativesValue === SELECTED_ANOTHER_VENDOR &&
        this.state.selectedVendor === OTHER &&
        !this.state.vendorInput
      ) {
        validationType = `vendor`;
        validationMessage = `Enter the name of the vendor`;
      }
    }
    this.update({validationType, validationMessage});
    return !validationMessage && !validationType;
  }

  getRequestData() {
    let planType;

    if (!this.attr(`is-legacy`)) {
      planType = Object.keys(this.state.planTypes).join(` `);
    } else {
      planType = Object.keys(this.state.planTypes)
        .filter((plan) => this.state.planTypes[plan])
        .join(` `);
    }

    const requestData = {
      account_id: this.attr(`account-id`),
      reasons: this.state.reasons,
      analytics_alternatives: this.state.alternativesValue,
      message: this.state.additionalInfo,
      plan_type: planType,
      request_type: this.attr(`request-type`),
    };

    if (this.state.reasons.includes(LACK_OF_A_FEATURE)) {
      requestData[`missing_feature`] = this.state.featureInput;
    }
    if (requestData[`analytics_alternatives`] === SELECTED_ANOTHER_VENDOR) {
      if (this.state.selectedVendor === OTHER) {
        requestData[`competitor`] = this.state.vendorInput;
      } else {
        requestData[`competitor`] = this.state.selectedVendor;
      }
    }
    return requestData;
  }

  async submitCancelRequest(requestData) {
    try {
      const rawResponse = await djangoFetch(`/billing/cancel_async/`, requestData);
      const res = await rawResponse.json();
      if (res.success) {
        // Don't use org tracking because it's not part of org's tracking
        if (
          typeof mixpanel !== `undefined` &&
          !(requestData.request_type === `deletion_type` && this.isFreeAccount())
        ) {
          let resourceType;
          const planType = requestData.plan_type;
          if (this.attr(`is-legacy`)) {
            resourceType = planType === `people` ? `People` : `Engagement`;
          } else {
            resourceType = this.getCancelledPlanResourceType(res.cancelled_plan_types);
          }
          const properties = {
            'Cancellation Reasons': requestData.reasons,
            'Cancellation: Analytics Choice Going Forward': requestData.analytics_alternatives,
            'Cancellation: Request Type': res.type,
            'Cancellation: Resource Type': resourceType,
          };
          if (requestData.analytics_alternatives === SELECTED_ANOTHER_VENDOR) {
            properties[`Cancellation: Competitor Choice`] = requestData.competitor;
          }
          mixpanel.track(`Click Submit Cancellation request`, properties);
        }
        this.triggerToast(res.message);
      } else {
        throw new Error(res);
      }
      return true;
    } catch {
      this.triggerError(`Error trying to cancel plans. Please try again or contact support.`);
      return false;
    }
  }

  triggerError(error) {
    this.dispatchEvent(new CustomEvent(`submit`, {detail: {error}}));
  }

  triggerToast(message) {
    this.dispatchEvent(new CustomEvent(`submit`, {detail: {message}}));
  }
}

export const CancelAccountDialogEl = defineAndMakeJsxFactory(`mp-cancel-account-dialog`, CancelAccountDialog);
export const CancelAccountDialogReact = panel2react<CancelAccountDialogJsxProps>(
  `mp-cancel-account-dialog`,
  CancelAccountDialog,
);
